<script setup lang="ts">
import { useI18n } from '@/i18n';
import { logError } from '@/lib/logError';
import { redirect } from '@/lib/redirect';
import { useBillingStore } from '@/stores/billing';
import { Alert, Button, LayoutGap, Loader, Modal, Prose } from '@dabble/quillui';
import { onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';

const { t } = useI18n();
const billingStore = useBillingStore();
const router = useRouter();

type Status = 'loading' | 'success' | 'error';
const status = ref<Status>('loading');
const message = ref('');
const open = ref(true);

const code = sessionStorage.getItem('referralCode') ?? '';

onMounted(async () => {
  // Always clear the code so a leftover value can't reattach to a different
  // account later (e.g. via "Switch Account → Add another account" in the same tab).
  sessionStorage.removeItem('referralCode');

  if (!code) {
    goContinue();
    return;
  }

  await billingStore.fetchBillingOverview();

  if (!billingStore.primarySubscription) {
    status.value = 'error';
    message.value = t('referral_apply_error_no_subscription');
    return;
  }

  const ok = await billingStore.applyCouponCode(code, { silent: true });
  if (ok) {
    status.value = 'success';
    message.value = t('referral_apply_success');
  } else {
    status.value = 'error';
    message.value = billingStore.lastApplyError ?? t('billing_coupon_error_generic');
  }
});

function goContinue() {
  // redirect() only throws when it refuses the stored target (INVALID_REDIRECT),
  // so navigating there anyway is exactly wrong — for a 2.x target it was a
  // login loop (DAB-771). Land on the portal, as the router's handOff() does.
  redirect().catch((err) => {
    logError('referral apply: redirect failed, falling back to the billing portal', err);
    router.push('/billing/portal');
  });
}
</script>

<template>
  <Modal v-model="open" :title="t('referral_apply_title')" :closable="false" size="small" @close="goContinue">
    <LayoutGap direction="column" size="medium" align="center">
      <template v-if="status === 'loading'">
        <Loader />
        <Prose align="center">{{ t('referral_apply_loading') }}</Prose>
      </template>

      <template v-else>
        <Alert :variant="status === 'success' ? 'success' : 'warning'">
          {{ message }}
        </Alert>
        <Button variant="primary" @click="goContinue">{{ t('referral_apply_continue') }}</Button>
      </template>
    </LayoutGap>
  </Modal>
</template>
